import { getApprovedImagesForProduct } from '@/lib/approved-product-images';
import { normalizeProductImages } from '@/lib/image-utils';
import type { ProductDisplay, ProductVariant } from '../types';

type RawProductVariant = Partial<ProductVariant> & {
  product_id?: string | null;
  unit_size?: string | null;
  size?: string | null;
  compare_at_price?: number | null;
  stock_quantity?: number | null;
  barcode?: string | null;
  is_default?: boolean | null;
};

type RawCategory = {
  name?: string | null;
} | null;

export type RawProductRecord = Omit<Partial<ProductDisplay>, 'images'> & {
  image?: string | null;
  image_url?: string | null;
  images?: string[] | string | null;
  category?: RawCategory;
  category_id?: string | null;
  stock_quantity?: number | null;
  is_available?: boolean | null;
  is_visible?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  compare_at_price?: number | null;
  brand?: string | null;
  popularity?: number | null;
  is_popular?: boolean | null;
  is_new_arrival?: boolean | null;
  is_on_sale?: boolean | null;
  is_on_offer?: boolean | null;
  visit_count?: number | null;
  variants?: RawProductVariant[] | null;
};

export function normalizeProductVariant(
  variant: RawProductVariant,
  fallbackProductId: string,
  index = 0
): ProductVariant {
  const volume = variant.volume ?? variant.size ?? variant.unit_size ?? '';

  return {
    ...variant,
    id: String(variant.id ?? `${fallbackProductId}-variant-${index + 1}`),
    productId: variant.productId ?? variant.product_id ?? fallbackProductId,
    volume,
    price: Number(variant.price ?? 0),
    compareAtPrice: variant.compareAtPrice ?? variant.compare_at_price ?? undefined,
    stock: Number(variant.stock ?? variant.stock_quantity ?? 0),
    sku: variant.sku ?? variant.barcode ?? `${fallbackProductId}-${volume || index + 1}`,
    isDefault: Boolean(variant.isDefault ?? variant.is_default ?? index === 0),
  };
}

export function normalizeProductRecord(
  product: RawProductRecord
): ProductDisplay & { image?: string; image_url?: string } {
  const id = String(product.id ?? '');
  const normalizedVariants = Array.isArray(product.variants)
    ? product.variants.map((variant, index) => normalizeProductVariant(variant, id, index))
    : [];
  const existingImages = normalizeProductImages(
    product.image ?? product.image_url ?? undefined,
    product.images ?? undefined
  );
  const images = getApprovedImagesForProduct({
    productName: product.name ?? '',
    variants: normalizedVariants.map((variant) => ({
      label: variant.volume,
    })),
    existingImages,
  });
  const fallbackCreatedAt = new Date(0).toISOString();
  const createdAt = product.createdAt ?? product.created_at ?? fallbackCreatedAt;
  const updatedAt = product.updatedAt ?? product.updated_at ?? createdAt;
  const stockQuantity =
    product.stockQuantity ??
    product.stock_quantity ??
    normalizedVariants.reduce((sum, variant) => sum + variant.stock, 0);

  return {
    ...product,
    id,
    name: product.name ?? '',
    description: product.description ?? '',
    categoryId: product.categoryId ?? product.category_id ?? '',
    images,
    stockQuantity: Number(stockQuantity ?? 0),
    isAvailable: product.isAvailable ?? product.is_available ?? true,
    isVisible: product.isVisible ?? product.is_visible ?? true,
    tags: Array.isArray(product.tags) ? product.tags : [],
    createdAt,
    updatedAt,
    compareAtPrice: product.compareAtPrice ?? product.compare_at_price ?? undefined,
    brand: product.brand ?? undefined,
    popularity: product.popularity ?? undefined,
    isPopular: product.isPopular ?? product.is_popular ?? false,
    isNewArrival: product.isNewArrival ?? product.is_new_arrival ?? false,
    isOnSale: product.isOnSale ?? product.is_on_sale ?? product.is_on_offer ?? false,
    visitCount: Number(product.visitCount ?? product.visit_count ?? 0),
    variants: normalizedVariants,
    categoryName: product.categoryName ?? product.category?.name ?? undefined,
    image: product.image ?? product.image_url ?? images[0],
    image_url: product.image_url ?? product.image ?? images[0],
  } as ProductDisplay & { image?: string; image_url?: string };
}
